import React, { useMemo, useState } from 'react'
import { digitsOnly } from '../lib/claimContactFormat'

/** 주민번호: 숫자만 입력이 기본이라 처음부터 편집 가능하게. 눈 아이콘은 뒷자리 가림/표시 토글. */
const defaultVisibleForKind = (maskKind) => maskKind === 'resident'

/** 앞 6자리는 그대로, 뒤 7자리는 항상 • (입력 여부와 관계없이 7칸) */
function maskResidentBackOnly(digits) {
  const d = digitsOnly(digits, 13)
  if (!d) return ''
  if (d.length <= 6) return d
  return `${d.slice(0, 6)}-${'•'.repeat(7)}`
}

function maskAccountLike(text) {
  const s = String(text ?? '')
  if (!s) return ''
  if (s.length <= 4) return '•'.repeat(s.length)
  return '•'.repeat(s.length - 4) + s.slice(-4)
}

function maskFullText(text) {
  const s = String(text ?? '')
  if (!s) return ''
  return '•'.repeat(s.length)
}

/**
 * 민감 필드: 기본은 마스킹, 눈 아이콘으로 실제 값 표시·편집.
 * @param {'resident'|'account'|'text'} maskKind
 */
export default function MaskableSensitiveInput({
  value,
  onChange,
  maskKind = 'text',
  disabled = false,
  className = '',
  inputMode,
  placeholder,
  autoComplete = 'off',
}) {
  const [visible, setVisible] = useState(() => defaultVisibleForKind(maskKind))

  const displayValue = useMemo(() => {
    if (visible) {
      // 주민번호는 편집 중 하이픈 없이 숫자만 두면 controlled input 커서/삭제가 안정적이다.
      return value ?? ''
    }
    if (maskKind === 'resident') return maskResidentBackOnly(value)
    if (maskKind === 'account') return maskAccountLike(value)
    return maskFullText(value)
  }, [visible, value, maskKind])

  const canEdit = !disabled && visible

  const handleChange = (e) => {
    if (!canEdit) return
    if (maskKind === 'resident') {
      onChange(digitsOnly(e.target.value, 13))
    } else {
      onChange(e.target.value)
    }
  }

  const handleFocus = () => {
    if (disabled || visible) return
    // 가림 상태에서 다시 클릭·탭하면 입력 가능하도록(주민번호)
    if (maskKind === 'resident') setVisible(true)
  }

  const baseInputClass = `w-full rounded border px-3 py-2 text-sm pr-10 ${disabled ? 'bg-slate-100 text-slate-600' : ''} ${className}`.trim()

  return (
    <div className="relative">
      <input
        className={baseInputClass}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        readOnly={!canEdit}
        maxLength={maskKind === 'resident' && visible ? 13 : undefined}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
      />
      <button
        type="button"
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
        aria-label={
          maskKind === 'resident'
            ? visible
              ? '주민등록번호 뒷자리 가리기'
              : '주민등록번호 뒷자리 보기'
            : visible
              ? '입력값 숨기기'
              : '입력값 보기'
        }
        disabled={disabled}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 5.1A10.4 10.4 0 0112 5c4.4 0 8.2 2.6 10 6.5a10.7 10.7 0 01-4.1 5.3M6.7 6.7C4.6 8.2 3 10.4 2 13c1.8 3.9 5.6 6.5 10 6.5 1.2 0 2.4-.2 3.5-.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  )
}
